<template>
  <div class="space-y-4 px-1 max-h-[400px] overflow-y-auto scrollable-area">
    <!-- Author's Voice -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]"
        >Author's Voice <span class="text-red-500">*</span>
        <i
          v-tooltip="'A high-level description of your brand — combining style, perspective, and personality across all content.'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i
      ></label>

      <Textarea
        input-class="no-scrollbar scroll-smooth"
        :model-value="brandInfo.strategy.brandVoice"
        placeholder="e.g., Fresh, transparent, and empowering — with a focus on sustainability, self-expression, and authenticity"
        rows="4"
        full-width
        @update:model-value="brandInfo.strategy.brandVoice = $event"
      />
    </div>

    <!-- Niche -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]"
        >Niche
        <i
          v-tooltip="'What space or industry does your brand operate in? Helps generate relevant content.'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i
      ></label>
      <Textarea
        input-class="no-scrollbar scroll-smooth"
        :model-value="brandInfo.strategy.niche"
        placeholder="e.g., Sustainable skincare for Gen Z consumers"
        full-width
        @update:model-value="brandInfo.strategy.niche = $event"
      />
    </div>

    <!-- Audience -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]"
        >Audience
        <i
          v-tooltip="'Who are you speaking to? Define your primary customer group or target market.'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i
      ></label>
      <Textarea
      input-class="no-scrollbar scroll-smooth"
        :model-value="brandInfo.strategy.audience"
        placeholder="e.g., Eco-conscious Gen Z individuals who value clean ingredients, ethical practices, and bold self-care messaging."
        rows="4"
        full-width
        @update:model-value="brandInfo.strategy.audience = $event"
      />
    </div>

    <!-- Tone -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]"
        >Tone
        <i
          v-tooltip="'Set the tone of your content — e.g., casual, motivational, educational.'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i
      ></label>
      <div class="relative">
        <div
          class="flex flex-wrap gap-2 p-2 min-h-[42px] rounded-lg border border-gray-300 bg-white"
        >
          <span
            v-for="tone in brandInfo.strategy.tone"
            :key="tone"
            class="px-2 py-1 bg-gray-100 text-gray-900 rounded-md text-sm"
          >
            {{ tone }}
            <i
              class="far fa-times ml-1 cursor-pointer"
              @click.stop="removeTone(tone)"
            ></i>
          </span>
          <input
            v-model="newTone"
            type="text"
            placeholder="Enter tone and press Enter"
            class="flex-1 min-w-[120px] border-none outline-none focus:ring-0 text-sm"
            @keydown.enter.prevent="addTone"
            @blur="addTone"
          />
        </div>
      </div>
    </div>

    <!--  Language -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]"
        >Language
        <i
          v-tooltip="'Preferred language style (e.g., simple, formal) to tailor content accordingly.'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i
      ></label>

      <div class="relative">
        <div
          class="flex flex-wrap gap-2 p-2 min-h-[42px] rounded-lg border border-gray-300 bg-white"
        >
          <span
            v-for="language in brandInfo.strategy.language"
            :key="language"
            class="px-2 py-1 bg-gray-100 text-gray-900 rounded-md text-sm"
          >
            {{ language }}
            <i
              class="far fa-times ml-1 cursor-pointer"
              @click.stop="removeLanguage(language)"
            ></i>
          </span>
          <input
            v-model="newLanguage"
            type="text"
            placeholder="Enter language and press Enter"
            class="flex-1 min-w-[120px] border-none outline-none focus:ring-0 text-sm"
            @keydown.enter.prevent="addLanguage"
            @blur="addLanguage"
          />
        </div>
      </div>
    </div>

    <!-- Emotion -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]"
        >Emotion
        <i
          v-tooltip="'What emotion should your content convey? (e.g., excitement, trust, calm).'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i
      ></label>
      <div class="relative">
        <div
          class="flex flex-wrap gap-2 p-2 min-h-[42px] rounded-lg border border-gray-300 bg-white"
        >
          <span
            v-for="emotion in brandInfo.strategy.emotion"
            :key="emotion"
            class="px-2 py-1 bg-gray-100 text-gray-900 rounded-md text-sm"
          >
            {{ emotion }}
            <i
              class="far fa-times ml-1 cursor-pointer"
              @click.stop="removeEmotion(emotion)"
            ></i>
          </span>
          <input
            v-model="newEmotion"
            type="text"
            placeholder="Enter emotion and press Enter"
            class="flex-1 min-w-[120px] border-none outline-none focus:ring-0 text-sm"
            @keydown.enter.prevent="addEmotion"
            @blur="addEmotion"
          />
        </div>
      </div>
    </div>

    <!-- Competitors -->
    <div>
      <label class="text-sm font-medium text-[#3A4557]">Competitors</label>
      <i
        v-tooltip="'Mention competitors or similar brands to help the AI understand your market.'"
        class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
      ></i>
      <div class="relative">
        <div
          class="flex flex-wrap gap-2 p-2 min-h-[42px] rounded-lg border border-gray-300 bg-white"
        >
          <span
            v-for="competitor in brandInfo.strategy.competitors"
            :key="competitor"
            class="px-2 py-1 bg-gray-100 text-gray-900 rounded-md text-sm"
          >
            {{ competitor }}
            <i
              class="far fa-times ml-1 cursor-pointer"
              @click.stop="removeCompetitor(competitor)"
            ></i>
          </span>
          <input
            v-model="newCompetitor"
            type="text"
            placeholder="Enter competitor and press Enter"
            class="flex-1 min-w-[120px] border-none outline-none focus:ring-0 text-sm"
            @keydown.enter.prevent="addCompetitor"
            @blur="addCompetitor"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Textarea } from '@contentstudio/ui'
import { useSetup } from '@modules/publisher/ai-content-library/composables/useSetup'

const { brandInfo } = useSetup()

const newTone = ref('')
const newLanguage = ref('')
const newCompetitor = ref('')
const newEmotion = ref('')

const addTone = () => {
  const tone = newTone.value.trim()
  if (tone && !brandInfo.strategy.tone.includes(tone)) {
    brandInfo.strategy.tone.push(tone)
  }
  newTone.value = ''
}

const removeTone = (tone) => {
  brandInfo.strategy.tone = brandInfo.strategy.tone.filter((t) => t !== tone)
}

const addLanguage = () => {
  const language = newLanguage.value.trim()
  if (language && !brandInfo.strategy.language.includes(language)) {
    brandInfo.strategy.language.push(language)
  }
  newLanguage.value = ''
}

const removeLanguage = (language) => {
  brandInfo.strategy.language = brandInfo.strategy.language.filter(
    (l) => l !== language
  )
}

const addCompetitor = () => {
  const competitor = newCompetitor.value.trim()
  if (competitor && !brandInfo.strategy.competitors.includes(competitor)) {
    brandInfo.strategy.competitors.push(competitor)
  }
  newCompetitor.value = ''
}

const removeCompetitor = (competitor) => {
  brandInfo.strategy.competitors = brandInfo.strategy.competitors.filter(
    (c) => c !== competitor
  )
}

const addEmotion = () => {
  const emotion = newEmotion.value.trim()
  if (emotion && !brandInfo.strategy.emotion.includes(emotion)) {
    brandInfo.strategy.emotion.push(emotion)
  }
  newEmotion.value = ''
}

const removeEmotion = (emotion) => {
  brandInfo.strategy.emotion = brandInfo.strategy.emotion.filter(
    (e) => e !== emotion
  )
}
</script>

<style>
.scrollable-area::-webkit-scrollbar-track {
  background-color: white;
}

.scrollable-area::-webkit-scrollbar-thumb {
  background-color: #dfdfdf;
}

.scrollable-area::-webkit-scrollbar {
  width: 10px;
}
</style>
